import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { catchError, mergeMap, of, switchMap, tap, throwError } from 'rxjs';

import { OnderwijsLocatiesActions } from '@store/actions/onderwijs-locaties.actions';
import { OnderwijsLocatiesInitialState, OnderwijsLocatiesStateModel } from '@store/models/onderwijs-locaties.model';
import { ApiClientService } from '@services/api-client.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';

@State<OnderwijsLocatiesStateModel>({
  name: 'onderwijsLocaties',
  defaults: OnderwijsLocatiesInitialState,
})
@Injectable()
export class OnderwijsLocatiesState {
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  constructor(private readonly apiDstService: ApiClientService) {}

  @Action(OnderwijsLocatiesActions.LoadOnderwijsLocaties)
  getOnderwijsLocaties(ctx: StateContext<OnderwijsLocatiesStateModel>) {
    const state = ctx.getState();

    // Skip API call if already loaded
    if (state.loaded) {
      return of(state.onderwijsLocaties);
    }

    ctx.patchState({ loading: true });
    return this.apiDstService.getOnderwijslocaties().pipe(
      tap((onderwijsLocaties) => {
        ctx.patchState({ loading: false });
        ctx.patchState({ onderwijsLocaties });
      }),
      mergeMap((onderwijsLocaties) => {
        ctx.patchState({ loaded: true });
        const state = ctx.getState();

        // Set the first onderwijsLocatie as selected
        if (state.onderwijsLocaties.length > 0) {
          ctx.dispatch(new OnderwijsLocatiesActions.SelectOnderwijsLocatie(state.onderwijsLocaties[0]));
        }
        return ctx.dispatch(new OnderwijsLocatiesActions.LoadOnderwijsLocatiesSuccess(onderwijsLocaties));
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        ctx.patchState({ error });
        return ctx.dispatch(new OnderwijsLocatiesActions.LoadOnderwijsLocatiesFailure(error));
      }),
    );
  }

  @Action(OnderwijsLocatiesActions.SelectOnderwijsLocatie) selectOnderwijsLocatie(
    ctx: StateContext<OnderwijsLocatiesStateModel>,
    action: OnderwijsLocatiesActions.SelectOnderwijsLocatie,
  ) {
    ctx.patchState({ selectedOnderwijsLocatieId: action.payload.onderwijsLocatieId });
  }

  @Action(OnderwijsLocatiesActions.SelectSchool)
  selectSchool(ctx: StateContext<OnderwijsLocatiesStateModel>, action: OnderwijsLocatiesActions.SelectSchool) {
    ctx.patchState({ selectedSchool: action.payload });
  }

  @Action(OnderwijsLocatiesActions.DeselectSchool)
  deselectSchool(ctx: StateContext<OnderwijsLocatiesStateModel>) {
    ctx.patchState({ selectedSchool: null });
  }

  @Action(OnderwijsLocatiesActions.UpdateOnderwijslocatieMetadata)
  updateOnderwijslocatieMetadata(
    ctx: StateContext<OnderwijsLocatiesStateModel>,
    action: OnderwijsLocatiesActions.UpdateOnderwijslocatieMetadata,
  ) {
    return this.apiDstService.updateOnderwijslocatieMetadata(action.onderwijslocatieId, action.payload).pipe(
      tap(() => {
        const state = ctx.getState();
        const onderwijsLocaties = state.onderwijsLocaties.map((ol) =>
          ol.onderwijsLocatieId === action.onderwijslocatieId
            ? { ...ol, aanleveringDeelnemersLas: action.payload.aanleveringDeelnemersLAS }
            : ol,
        );
        // Update selectedSchool with the new data instead of nulling it
        const updatedSelectedSchool =
          state.selectedSchool?.onderwijsLocatieId === action.onderwijslocatieId
            ? { ...state.selectedSchool, aanleveringDeelnemersLas: action.payload.aanleveringDeelnemersLAS }
            : state.selectedSchool;
        ctx.patchState({ onderwijsLocaties, selectedSchool: updatedSelectedSchool });
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('TOASTER_MESSAGES.SCHOOL_UPDATE_SUCCESS_TITLE'),
          detail: this.translate.instant('TOASTER_MESSAGES.SCHOOL_UPDATE_SUCCESS'),
        });
        ctx.dispatch(
          new OnderwijsLocatiesActions.UpdateOnderwijslocatieMetadataSuccess(
            action.onderwijslocatieId,
            action.payload.aanleveringDeelnemersLAS,
          ),
        );
      }),
      catchError((error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('TOASTER_MESSAGES.SCHOOL_UPDATE_ERROR_TITLE'),
          detail: this.translate.instant('TOASTER_MESSAGES.SCHOOL_UPDATE_ERROR'),
        });
        return ctx
          .dispatch(new OnderwijsLocatiesActions.UpdateOnderwijslocatieMetadataFailure(error))
          .pipe(switchMap(() => throwError(() => error)));
      }),
    );
  }

  @Action(OnderwijsLocatiesActions.DeleteOnderwijslocatie)
  deleteOnderwijslocatie(
    ctx: StateContext<OnderwijsLocatiesStateModel>,
    action: OnderwijsLocatiesActions.DeleteOnderwijslocatie,
  ) {
    return this.apiDstService.deleteOnderwijslocatie(action.onderwijslocatieId).pipe(
      tap(() => {
        const state = ctx.getState();
        const onderwijsLocaties = state.onderwijsLocaties.filter(
          (ol) => ol.onderwijsLocatieId !== action.onderwijslocatieId,
        );
        ctx.patchState({ onderwijsLocaties });
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('TOASTER_MESSAGES.SCHOOL_DELETE_SUCCESS_TITLE'),
          detail: this.translate.instant('TOASTER_MESSAGES.SCHOOL_DELETE_SUCCESS'),
        });
        ctx.dispatch(new OnderwijsLocatiesActions.DeleteOnderwijslocatieSuccess(action.onderwijslocatieId));
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        const errorMessageKey =
          error.status === 403
            ? 'TOASTER_MESSAGES.SCHOOL_DELETE_ERROR_FORBIDDEN'
            : 'TOASTER_MESSAGES.SCHOOL_DELETE_ERROR';

        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('TOASTER_MESSAGES.SCHOOL_DELETE_ERROR_TITLE'),
          detail: this.translate.instant(errorMessageKey),
        });
        return ctx.dispatch(new OnderwijsLocatiesActions.DeleteOnderwijslocatieFailure(error));
      }),
    );
  }
}
